import React, { useState, useContext, useEffect } from "react";
import { Button, CloseButton, Portal, SimpleGrid, Dialog, Input, Field, Box, Textarea, FileUpload, Icon, createListCollection, Select, HStack, Spinner } from "@chakra-ui/react";
import { LuUpload, LuText, LuClock, LuCoins, LuUsers, LuRuler, LuFolders } from "react-icons/lu";
import { useAppStore } from "../../../../context/store";
import { floatingStyles, showToast } from "../../../../utilies/Utility";
import Type from "../../../../utilies/Types";
import { serviceAddTask } from "../../../../api/services/task";

const TaskManagementNewTask = ({ state, polygonState }) => {
  const userData = useAppStore((s) => s.userData);
  const users = useAppStore((s) => s.users);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const projects = useAppStore((s) => s.projects);

  const [bbox, setBbox] = useState("");
  const [isProgress, setIsProgress] = useState(false);
  const [projectsOptions, setProjectsOptions] = useState(
    createListCollection({
      items: [],
    }),
  );
  const [sendingData, setSendingData] = useState({
    start_date: "",
    end_date: "",
    title: "",
    description: "",
    bbox: "",
    length: "",
    token: "",
    project_id: "",
    city: "",
    contributors: [],
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);

  const cancel = () => {
    polygonState[1](null);
  };

  useEffect(() => {
    if (projects) {
      const collection = createListCollection({
        items: projects.map((proj) => ({
          label: proj.project_name,
          value: proj.id,
        })),
      });

      setProjectsOptions(collection);
    }
  }, [projects]);

  const add = () => {
    const bboxArray = sendingData.bbox.split(",").map((pair) => {
      const [lon, lat] = pair.trim().split(" ").map(Number);
      return [lat, lon];
    });

    const startEpoch = Math.floor(new Date(sendingData.start_date).getTime() / 1000);
    const endEpoch = Math.floor(new Date(sendingData.end_date).getTime() / 1000);

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        title: sendingData.title,
        description: sendingData.description,
        length: sendingData.length,
        token: sendingData.token,
        project_id: sendingData.project_id,
        start_date: startEpoch,
        end_date: endEpoch,
        bbox: bboxArray,
        city: sendingData.city,
      }),
    );
    formData.append("contributors", sendingData.contributors.join(","));
    formData.append("task_admin", userData.user.username);

    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    if (selectedMediaFile) {
      formData.append("media_file", selectedMediaFile);
    }

    setIsProgress(true);
    serviceAddTask(formData, userData.token)
      .then((res) => {
        setIsProgress(false);
        if (res.data.status === "success") {
          state[1](false);
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
        }
      })
      .catch((err) => {
        setIsProgress(false);
        console.error("Error", err);
      });
  };

  const [contributors, setContributors] = useState(
    createListCollection({ items: [] }), // initially empty
  );
  useEffect(() => {
    if (users) {
      const newContributors = createListCollection({
        items: Object.values(users)
          // sort by username alphabetically
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((user) => ({
            // Use full name if available, otherwise fallback to username
            label: (`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username) + " (" + user.username + ")",
            // Use username as value (unique)
            value: user.username,
          })),
      });

      setContributors(newContributors); // update state
    }
  }, [users]);

  const [date4, setDate4] = useState("");
  useEffect(() => {
    const today = new Date();
    today.setDate(today.getDate() + 4);
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const endDate = `${yyyy}-${mm}-${dd}`;
    setDate4(endDate);

    setSendingData((prev) => ({
      ...prev,
      start_date: new Date().toISOString().split("T")[0],
      end_date: endDate,
    }));
  }, []);

  useEffect(() => {
    let temp = "";
    for (const key in polygonState[0]) {
      temp += polygonState[0][key].lon + " " + polygonState[0][key].lat + ", ";
    }
    const bboxStr = temp.slice(0, -2);
    setBbox(bboxStr);
    setSendingData((prev) => ({ ...prev, bbox: bboxStr }));
  }, [polygonState[0]]);

  useEffect(() => {
    if (state[0]) {
      setSendingData((prev) => ({ ...prev, project_id: selectedProject?.id }));
    }
  }, [state[0]]);

  return (
    <Dialog.Root
      scrollBehavior="inside"
      placement={"top"}
      motionPreset="slide-in-right"
      closeOnInteractOutside={false}
      modal={false}
      open={state[0]}
      onOpenChange={(e) => {
        state[1](e.open);
      }}
    >
      <Portal>
        <Dialog.Positioner pointerEvents="none" height={"calc(100% - 30px)"}>
          <Dialog.Content mr={2} mb={0} mt={"70px"} height={"100%"} width={"400px"} maxWidth={"400px"} borderRadius={"2xl"}>
            <Dialog.Header>
              <Dialog.Title> New Task </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <SimpleGrid columns={2} gap={1} mb={4}>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input className="peer" type="date" defaultValue={new Date().toISOString().split("T")[0]} onChange={(e) => setSendingData((prev) => ({ ...prev, start_date: e.target.value }))} />
                    <Field.Label css={floatingStyles}>
                      <LuClock /> Start Time
                    </Field.Label>
                  </Box>
                </Field.Root>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input className="peer" type="date" defaultValue={date4} onChange={(e) => setSendingData((prev) => ({ ...prev, end_date: e.target.value }))} />
                    <Field.Label css={floatingStyles}>
                      <LuClock /> End Time
                    </Field.Label>
                  </Box>
                </Field.Root>
              </SimpleGrid>
              <Field.Root mb={4}>
                <Box pos="relative" w="full">
                  <Input className="peer" type="text" onChange={(e) => setSendingData((prev) => ({ ...prev, title: e.target.value }))} />
                  <Field.Label css={floatingStyles}>
                    <LuText /> Task Name
                  </Field.Label>
                </Box>
              </Field.Root>
              <Field.Root mb={4}>
                <Box pos="relative" w="full">
                  <Textarea className="peer" rows={7} onChange={(e) => setSendingData((prev) => ({ ...prev, description: e.target.value }))} />
                  <Field.Label css={floatingStyles}>
                    <LuText /> Description
                  </Field.Label>
                </Box>
              </Field.Root>
              <Field.Root mb={4}>
                <Box pos="relative" w="full">
                  <Textarea className="peer" rows={5} disabled value={bbox} />
                  <Field.Label css={floatingStyles}>
                    <LuText /> BBOX
                  </Field.Label>
                </Box>
              </Field.Root>

              <SimpleGrid columns={2} gap={1} my={4}>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input className="peer" type="text" onChange={(e) => setSendingData((prev) => ({ ...prev, length: e.target.value }))} />
                    <Field.Label css={floatingStyles}>
                      <LuRuler /> Length
                    </Field.Label>
                  </Box>
                </Field.Root>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input className="peer" type="text" onChange={(e) => setSendingData((prev) => ({ ...prev, token: e.target.value }))} />
                    <Field.Label css={floatingStyles}>
                      <LuCoins /> Token
                    </Field.Label>
                  </Box>
                </Field.Root>
              </SimpleGrid>

              <Select.Root
                collection={projectsOptions}
                defaultValue={[selectedProject?.id]}
                size="sm"
                width="full"
                onValueChange={(val) => setSendingData((prev) => ({ ...prev, project_id: val.value[0] }))}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuFolders /> Project
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Project" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {projectsOptions.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>

              <Select.Root
                multiple
                collection={contributors}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                onValueChange={(val) => {
                  setSendingData((prev) => ({ ...prev, contributors: val.value }));
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuUsers /> Contributors
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {contributors.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>

              <FileUpload.Root
                maxW="xl"
                alignItems="stretch"
                maxFiles={1}
                onFileChange={(u) => {
                  setSelectedFile(u.acceptedFiles[0]);
                }}
              >
                <FileUpload.HiddenInput />
                <FileUpload.Dropzone>
                  <Icon size="md" color="fg.muted">
                    <LuUpload />
                  </Icon>
                  <FileUpload.DropzoneContent>
                    <Box>Drag and drop tutorial video here</Box>
                    <Box color="fg.muted">.mp4, .webm, .ogg up to 100MB</Box>
                  </FileUpload.DropzoneContent>
                </FileUpload.Dropzone>
                <FileUpload.List />
              </FileUpload.Root>

              <FileUpload.Root
                maxW="xl"
                alignItems="stretch"
                maxFiles={20}
                mt={3}
                onFileChange={(u) => {
                  setSelectedMediaFile(u.acceptedFiles[0]);
                }}
              >
                <FileUpload.HiddenInput />
                <FileUpload.Dropzone>
                  <Icon size="md" color="fg.muted">
                    <LuUpload />
                  </Icon>
                  <FileUpload.DropzoneContent>
                    <Box>Task Attachment</Box>
                    <Box>Drag and drop files here</Box>
                    <Box color="fg.muted">PDF, JPEG, PNG, SVG, Geojson, OSM, MP4, MP3 </Box>
                  </FileUpload.DropzoneContent>
                </FileUpload.Dropzone>
                <FileUpload.List />
              </FileUpload.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" onClick={cancel}>
                  Cancel
                </Button>
              </Dialog.ActionTrigger>
              <Button variant="solid" colorPalette={"green"} onClick={add} disabled={isProgress}>
                {isProgress ? <Spinner size={"sm"} /> : "Add"}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default TaskManagementNewTask;
